import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconEdit,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconSendFilled,
  IconDeviceFloppy,
} from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { findMEmail } from "../../../api/ModelsApi";
import EmailAccountSelectorWorkflow from "../workflows/CreateCampaign/flowHorizontal/components/EmailAccountSelectorWorkflow";
import { useLanguage } from "../../../stores/LanguageStore";
import account_placeholder from "../../../assets/icons/account_placeholder.png";
import EmailAccountSelector from "../../../components/Selectors/EmailAccountSelector";
import { replaceTokens } from "../../../components/View/previewUtils";
import NewsletterList from "./NewsletterList";
import NewsletterExclusion from "./NewsletterExclusion";
import NewsletterProgram from "./NewsletterProgram";
import NewsletterAdditionals from "./NewsletterAdditionals";
import { defaultProgramData } from "../workflows/CreateCampaign/flow/flowSettings";
import {
  createWorkflow,
  editProgrammation,
  editWorkflow,
  findWorkflow,
  programWorkflow,
} from "../../../api/WorkflowAPI";
import { defaultNodeConfigs } from "../workflows/CreateCampaign/flowHorizontal/flowSettings";
import { notifications } from "@mantine/notifications";
import { useProfileStore } from "../../../stores/UserStore";
import { useCampaignStore } from "../../../stores/CampaignStore";
import { editCampaign, findCampaign } from "../../../api/StatsAPI";
import { addHoursToDate, getTimezoneOffset } from "../../../functions/dates";
import { clearWorkflow } from "../workflows/CreateCampaign/flowHorizontal/flowUtils";

const Newsletter = () => {
  const lang = useLanguage((s) => s.language);
  const { modelId: modelIdParam, campaignId, workflowId } = useParams();
  const [modelId, setModelId] = useState(modelIdParam);

  const setTimeStart = useCampaignStore((s) => s.setTimeStart);
  const programData = useCampaignStore((s) => s.programData);

  const [loading, setLoading] = useState(true);
  const [modelProfile, setModelProfile] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [contactListIds, setContactListIds] = useState([]);
  const [blacklistIds, setBlacklistIds] = useState([]);
  const [sentCampaignIds, setSentCampaignIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [workflowProfile, setWorkflowProfile] = useState(null);

  // Sélecteur Desktop / Mobile
  const [viewMode, setViewMode] = useState("desktop");

  // --- NOUVEAUX ÉTATS POUR LE NOM ---
  const [workflowName, setWorkflowName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);

  const handleSenderChange = (v) => {
    setIntegration(v?.id);
  };

  useEffect(() => {
    if (!modelId) {
      setLoading(false);
    } else {
      findMEmail(modelId)
        .then((response) => {
          const profile = response.data.model_profile;
          setModelProfile(profile);
          // Initialiser le nom du workflow avec celui du modèle
          if (!workflowId) {
            setWorkflowName(profile.name || lang.newsletter.default_name);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [modelId]);

  // Fonction pour valider la saisie
  const handleNameSubmit = () => {
    if (!workflowName.trim()) {
      setWorkflowName(lang.newsletter.default_name); // Valeur de repli si le champ est vide
    }
    setIsEditingName(false);
  };

  const navigate = useNavigate();
  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());

    if (contactListIds.length === 0) {
      notifications.show({
        title: lang.newsletter.error_title,
        message: lang.newsletter.error_no_list,
        color: "red",
      });
      return;
    }

    const steps = [
      {
        step_id: {
          type: "new",
          id: 1,
        },
        step_type: "action",
        position_x: 40,
        position_y: 92,
        parent_ids: [],
        action_type: "dummy",
        is_initial: true,
        limit_send_per_day: defaultNodeConfigs?.limit_send_per_day,
        sending_weekends_allowed: defaultNodeConfigs?.sending_weekends_allowed,
        time_start_sending: defaultNodeConfigs?.time_start_sending,
        time_stop_sending: defaultNodeConfigs?.time_stop_sending,
        blacklist_ids: defaultNodeConfigs?.blacklist_ids,
        email_linkedin_exclude: defaultNodeConfigs?.email_linkedin_exclude,
        show_reply_as_conversation:
          defaultNodeConfigs?.show_reply_as_conversation,
        allow_massmailing: defaultNodeConfigs?.allow_massmailing,
        use_smtp_fallback_server: defaultNodeConfigs?.use_smtp_fallback_server,
      },
      {
        step_id: {
          type: "new",
          id: 2,
        },
        step_type: "event",
        position_x: 0,
        position_y: 0,
        parent_ids: [
          {
            type: "new",
            id: 1,
          },
        ],
        event_type: "all_contacts",
        is_initial: false,
        when_minutes: 1,
        filter: {
          mode: "and",
          values: [
            {
              field_name: "date_added_to_contact_list",
              type: "less_than",
              value: programData?.limit_date || new Date().toISOString(),
            },
          ],
        },
      },
      {
        step_id: {
          type: "new",
          id: 3,
        },
        step_type: "action",
        position_x: 0,
        position_y: 0,
        parent_ids: [
          {
            type: "new",
            id: 2,
          },
        ],
        action_type: "email",
        model_id: parseInt(modelId),
        integration_id: integration,
        // pool_id: step.data?.pool_id,
        is_initial: false,
        // signature_id: step.data?.signature_id,
        limit_send_per_day: programData?.daily_send_limit || 0,
        sending_weekends_allowed: defaultNodeConfigs?.sending_weekends_allowed,
        time_start_sending: defaultNodeConfigs?.time_start_sending,
        time_stop_sending: defaultNodeConfigs?.time_stop_sending,
        blacklist_ids: defaultNodeConfigs?.blacklist_ids,
        email_linkedin_exclude: defaultNodeConfigs?.email_linkedin_exclude,
        show_reply_as_conversation:
          defaultNodeConfigs?.show_reply_as_conversation,
        allow_massmailing: defaultNodeConfigs?.allow_massmailing,
        use_smtp_fallback_server: defaultNodeConfigs?.use_smtp_fallback_server,
      },
    ];

    let workflowData = {
      auto_remove_responders: true,
      name: workflowName,
      steps: steps,
      is_newsletter: true,
    };

    let programDataToSend = {
      ...programData,
      ...data,
      contactlist_ids: contactListIds,
      blacklist_ids: blacklistIds,
      exclude_programmation_ids: sentCampaignIds,
      date_start: programData?.date_start || new Date().toISOString(),
      date_stop: programData?.date_stop || data?.date_stop || undefined,
      time_start_sending: programData?.time_start_sending,
      time_stop_sending: useCampaignStore.getState().timeStop || undefined,
      daily_send_limit: programData?.daily_send_limit || 0,
      time_sending_timezone:
        useProfileStore.getState().profile?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      resend_to_bounce: data?.resend_to_bounce === "on" ? true : false,
      stop_steps_at_end_date:
        data?.stop_steps_at_end_date === "on" ? true : false,
      priority_email: data?.priority_email === "on" ? true : false,
      limit_total_sending: parseInt(data?.limit_total_sending) || 0,
    };

    Object.keys(programDataToSend).forEach((key) => {
      if (
        (key.includes("time_start_sending_") ||
          key.includes("time_stop_sending_")) &&
        programDataToSend[key] &&
        programDataToSend[key]?.length < 7
      ) {
        programDataToSend[key] = programDataToSend[key] + ":00";
      }
    });

    console.log(workflowData);
    console.log(programDataToSend);

    setSubmitting(true);
    createWorkflow(workflowData)
      .then((response) => {
        const newWorkflowId = response.data.workflow_id;

        programWorkflow({
          workflow_id: newWorkflowId,
          data: programDataToSend,
        })
          .then((r) => {
            let newProgramId = r?.data?.workflow_programmation_id;
            navigate("/statistics/campaigns/running?highlight=" + newProgramId);
          })
          .finally(() => setSubmitting(false));
      })
      .catch((err) => setSubmitting(false));
  };

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());

    if (contactListIds.length === 0) {
      notifications.show({
        title: lang.newsletter.error_title,
        message: lang.newsletter.error_no_list,
        color: "red",
      });
      return;
    }

    const steps = [
      workflowProfile?.steps?.[0],
      {
        ...workflowProfile?.steps?.[1],
        filter: {
          mode: "and",
          values: [
            {
              field_name: "date_added_to_contact_list",
              type: "less_than",
              value: programData?.limit_date || new Date().toISOString(),
            },
          ],
        },
      },
      {
        ...workflowProfile?.steps?.[2],
        model_id: parseInt(modelId),
        integration_id: integration,
      },
    ];

    let workflowData = {
      id: parseInt(workflowId),
      auto_remove_responders: true,
      name: workflowName,
      steps: steps,
      is_newsletter: true,
    };

    let programDataToSend = {
      ...programData,
      ...data,
      contactlist_ids: contactListIds,
      blacklist_ids: blacklistIds,
      exclude_programmation_ids: sentCampaignIds,
      date_start: programData?.date_start || new Date().toISOString(),
      date_stop: programData?.date_stop || data?.date_stop || undefined,
      time_start_sending: programData?.time_start_sending,
      time_stop_sending: useCampaignStore.getState().timeStop || undefined,
      daily_send_limit: programData?.daily_send_limit || 0,
      time_sending_timezone:
        useProfileStore.getState().profile?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      resend_to_bounce: data?.resend_to_bounce === "on" ? true : false,
      stop_steps_at_end_date:
        data?.stop_steps_at_end_date === "on" ? true : false,
      priority_email: data?.priority_email === "on" ? true : false,
      limit_total_sending: parseInt(data?.limit_total_sending) || 0,
    };

    Object.keys(programDataToSend).forEach((key) => {
      if (
        (key.includes("time_start_sending_") ||
          key.includes("time_stop_sending_")) &&
        programDataToSend[key] &&
        programDataToSend[key]?.length < 7
      ) {
        programDataToSend[key] = programDataToSend[key] + ":00";
      }
    });

    console.log(workflowData);
    console.log(programDataToSend);

    setSubmitting(true);
    editWorkflow(workflowData)
      .then((response) => {
        editCampaign({
          workflow_id: workflowId,
          data: programDataToSend,
          programmation_id: campaignId,
        })
          .then((r) => {
            let newProgramId = campaignId;
            navigate("/statistics/campaigns/running?highlight=" + newProgramId);
          })
          .finally(() => setSubmitting(false));
      })
      .catch((err) => setSubmitting(false));
  };

  useEffect(() => {
    setTimeStart(defaultProgramData.time_start_sending);
  }, [campaignId]);

  useEffect(() => {
    if (workflowId && campaignId) {
      setLoading(true);
      findCampaign(workflowId, campaignId).then((response) => {
        let config = response.data.workflow_programmation_profile;

        let timezoneOffset = getTimezoneOffset(config?.time_sending_timezone);
        let times = {};

        let date_start = useProfileStore.getState().profile?.timezone
          ? addHoursToDate(
              config.date_start,
              -timezoneOffset + new Date().getTimezoneOffset() / 60,
            )
          : new Date(config.date_start).toISOString();

        const setTimeStart = useCampaignStore.getState().setTimeStart;
        setTimeStart(config.time_start_sending);
        const setTimeStop = useCampaignStore.getState().setTimeStop;
        setTimeStop(config.time_stop_sending);

        setBlacklistIds(config?.blacklist_ids || []);
        setSentCampaignIds(config?.exclude_programmation_ids || []);

        findWorkflow(workflowId)
          .then((response) => {
            let newWorkflowProfile = response.data.workflow_profile;
            setWorkflowName(
              response.data.workflow_profile.name || lang.newsletter.default_name,
            );
            setWorkflowProfile(newWorkflowProfile);
            const mainAction = response.data.workflow_profile.steps.find(
              (x) => x.action_type === "email",
            );
            setModelId(mainAction?.model_id);
            setIntegration(mainAction?.pool_id || mainAction?.integration_id);
            const limit_date = newWorkflowProfile?.steps.find(
              (x) => x.step_type === "event",
            )?.filter?.values[0]?.value;
            useCampaignStore.getState().setTimeStart(config.time_start_sending);
            useCampaignStore.getState().setProgramData({
              ...config,
              ...times,
              date_start,
              limit_date,
            });
            useCampaignStore.getState().setOldProgramData({
              ...config,
              ...times,
              date_start,
              limit_date,
            });
          })
          .finally(() => setLoading(false));
      });
    }
  }, [campaignId, workflowId]);

  useEffect(() => {
    return () => {
      clearWorkflow();

      useCampaignStore.getState().setProgramData(defaultProgramData);
      useCampaignStore
        .getState()
        .setTimeStart(defaultProgramData.time_start_sending);
      useCampaignStore
        .getState()
        .setTimeStop(defaultProgramData.time_stop_sending);
    };
  }, []);

  return (
    <Grid
      gutter="xl"
      p="xl"
      pt={16}
      component={"form"}
      onSubmit={workflowId ? handleSubmitEdit : handleSubmit}
      pos={"relative"}
    >
      {/* ---------------- PARTIE GAUCHE ---------------- */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Stack gap="lg" h={"100%"}>
          {loading ? (
            <>
              <Skeleton height={40} radius="md" />
              <Skeleton height={150} radius="md" />
              <Skeleton height={500} radius="md" />
            </>
          ) : (
            <>
              {/* 1. Header : Titre & Sélecteur d'appareil */}
              <Group justify="space-between" align="center">
                <Paper radius={"xl"} flex={1} p={"sm"} pl={"xl"}>
                  {isEditingName ? (
                    // --- MODE ÉDITION ---
                    <TextInput
                      value={workflowName}
                      onChange={(e) => setWorkflowName(e.currentTarget.value)}
                      onBlur={handleNameSubmit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleNameSubmit();
                      }}
                      autoFocus
                      variant="filled"
                      radius="xl"
                      size="md"
                      styles={{
                        input: {
                          fontWeight: 700,
                          fontSize: "var(--mantine-font-size-xl)",
                        },
                      }}
                    />
                  ) : (
                    // --- MODE AFFICHAGE ---
                    <Group
                      gap="xs"
                      onClick={() => setIsEditingName(true)}
                      style={{ cursor: "pointer", width: "fit-content" }}
                    >
                      <Text fw={700} size="xl">
                        {workflowName}
                      </Text>
                      <ActionIcon variant="subtle" color="gray" radius="md">
                        <IconEdit size={18} />
                      </ActionIcon>
                    </Group>
                  )}
                </Paper>

                <SegmentedControl
                  value={viewMode}
                  onChange={setViewMode}
                  radius="xl"
                  size="lg"
                  bg={"gray.2"}
                  color="white"
                  data={[
                    {
                      value: "desktop",
                      label: (
                        <Center>
                          <IconDeviceDesktop
                            color={viewMode === "desktop" ? undefined : "gray"}
                            size={24}
                          />
                        </Center>
                      ),
                    },
                    {
                      value: "mobile",
                      label: (
                        <Center>
                          <IconDeviceMobile
                            color={viewMode === "mobile" ? undefined : "gray"}
                            size={24}
                          />
                        </Center>
                      ),
                    },
                  ]}
                />
              </Group>

              {/* 2. Bloc Métadonnées (Expéditeur, Objet, Aperçu) */}
              <Paper radius="lg" p="xl" py={"sm"}>
                <Stack gap="md">
                  {/* Ligne Expéditeur */}
                  <Flex align="center">
                    <Text w={120} c="dimmed" tt="uppercase">
                      {lang.newsletter.sender_label}
                    </Text>
                    <Box style={{ flex: 1 }}>
                      <EmailAccountSelector
                        value={integration}
                        className="nodrag nopan"
                        onChange={handleSenderChange}
                        leftSection={
                          <Tooltip
                            label={
                              lang.campaign?.selectors?.senders
                                ?.tooltip_linkedin ||
                              lang.newsletter.sender_tooltip_fallback
                            }
                          >
                            <Avatar src={account_placeholder} size={20} />
                          </Tooltip>
                        }
                        leftSectionWidth={36}
                        required
                        size="md"
                        radius="lg"
                        name="integration"
                        placeholder={
                          lang.campaign?.selectors?.senders?.placeholder ||
                          lang.newsletter.sender_placeholder_fallback
                        }
                        isPool={integration?.accounts_list !== undefined}
                        variant="unstyled"
                        styles={{
                          input: {
                            background: "transparent",
                            fontWeight: 600,
                            border: integration
                              ? "1px solid var(--mantine-color-green-2)"
                              : "1px solid var(--mantine-color-red-4)",
                          },
                        }}
                      />
                    </Box>
                  </Flex>

                  <Divider color="gray.2" />

                  {/* Ligne Objet */}
                  <Flex align="center">
                    <Text miw={120} c="dimmed" tt="uppercase">
                      {lang.newsletter.subject_label}
                    </Text>
                    <Text fw={600} lineClamp={1}>
                      🚀 {modelProfile?.subject || lang.newsletter.no_subject}
                    </Text>
                  </Flex>

                  {/* Ligne Aperçu */}
                  <Flex align="center">
                    <Text miw={120} c="dimmed" tt="uppercase">
                      {lang.newsletter.preview_label}
                    </Text>
                    <Text lineClamp={1}>
                      {modelProfile?.text || lang.newsletter.no_preview}
                    </Text>
                  </Flex>
                </Stack>
              </Paper>

              {/* 3. Bloc Prévisualisation HTML */}
              <Paper
                radius="lg"
                p={"lg"}
                py={"sm"}
                style={{
                  overflow: "hidden",
                  width: viewMode === "mobile" ? 375 : "100%",
                  margin: viewMode === "mobile" ? "0 auto" : "0",
                  transition: "width 0.3s ease",
                  flex: "1 1 auto",
                  backgroundColor: "#fff",
                }}
              >
                {modelProfile?.html ? (
                  <iframe
                    srcDoc={`<div style="font-family: sans-serif;">
                        ${(
                          replaceTokens({
                            text: modelProfile?.html,
                          }) || ""
                        ).replace(
                          /<a /g,
                          '<a target="_blank" rel="noopener noreferrer" ',
                        )}
                      </div>`}
                    title={lang.newsletter.iframe_title}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      fontFamily: "Arial, sans-serif",
                      minHeight: 420,
                    }}
                  />
                ) : (
                  <Flex justify="center" align="center" h="100%">
                    <Text c="dimmed">{lang.newsletter.no_html}</Text>
                  </Flex>
                )}
              </Paper>
            </>
          )}
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        {loading ? (
          <>
            <Skeleton height={40} radius="md" />
            <Skeleton height={150} radius="md" />
            <Skeleton height={500} radius="md" />
          </>
        ) : (
          <Paper
            radius="lg"
            p="lg"
            h="100%"
            minHeight={800}
            withBorder
            style={{
              boxShadow: "#00000012 4px 4px 8px",
            }}
          >
            <LoadingOverlay visible={submitting} />
            <Text fw={600} size="lg">
              {workflowId ? lang.newsletter.edit_title : lang.newsletter.program_title}
            </Text>
            <Text c={"dimmed"}>
              {lang.newsletter.subtitle}
            </Text>

            <NewsletterList
              contactListIds={contactListIds}
              setContactListIds={setContactListIds}
            />

            <NewsletterProgram />

            <NewsletterExclusion
              blacklistIds={blacklistIds}
              setBlacklistIds={setBlacklistIds}
              sentCampaignIds={sentCampaignIds}
              setSentCampaignIds={setSentCampaignIds}
            />

            <NewsletterAdditionals />

            <Group justify="center" mt={"lg"}>
              {workflowId ? (
                <Button
                  leftSection={<IconDeviceFloppy />}
                  size="lg"
                  radius="xl"
                  color="primary"
                  type="submit"
                  disabled={submitting || loading}
                >
                  {lang.newsletter.save_button}
                </Button>
              ) : (
                <Button
                  leftSection={<IconSendFilled />}
                  size="lg"
                  radius="xl"
                  color="primary"
                  type="submit"
                  disabled={submitting || loading}
                >
                  {lang.newsletter.program_button}
                </Button>
              )}
            </Group>
          </Paper>
        )}
      </Grid.Col>
    </Grid>
  );
};

const Center = ({ children }) => (
  <Flex justify="center" align="center" h="100%" c={"primary"}>
    {children}
  </Flex>
);

export default Newsletter;